/**
 * Diagnostic data for the exploded view.
 *
 * Two independent layers, fetched/derived separately so one failing never
 * blanks the other:
 *
 *   density  - recorded failure-case counts per part, from the KG-backed
 *              /assembly/{vehicle}/bom endpoint.
 *   dtc      - the asset's ACTIVE fault codes (data/faultSets.js), mapped onto parts.
 *
 * Both degrade to "no data" rather than throwing. The 3D view must keep working
 * with the backend down — that is a spec requirement, not a nicety.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Fault -> part tables live with the data, one set per assembly.
export { DTC_PART_MAP } from '../../data/faultSets';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const REQUEST_TIMEOUT_MS = 12000;
const NO_ALERTS = [];
const NO_MAP = {};

const SEVERITY_RANK = { critical: 3, warning: 2, info: 1 };

/** Highest-severity wins when several codes land on one part. */
function worst(a, b) {
  if (!a) return b;
  if (!b) return a;
  return (SEVERITY_RANK[b.severity] || 0) > (SEVERITY_RANK[a.severity] || 0) ? b : a;
}

async function getJson(url, signal) {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Options:
 *   fetchBom        optional (signal) => Promise<{ok, data}> — part-knowledge
 *                   assets inject their client's /bom (works in fixture mode)
 *   linkByPresence  treat every BOM node as linked. Part-keyed packs have no
 *                   kg_ids (knowledge is keyed by cad id), so presence in the
 *                   BOM is what "linked" means for them.
 */
export default function useDiagnostics(vehicle = 'ace_ev', {
  enabled = true, faultSet = null, fetchBom = null, linkByPresence = false,
} = {}) {
  const partMap = faultSet?.partMap ?? NO_MAP;

  const [density, setDensity] = useState(null);
  const [densityError, setDensityError] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchedFor = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    // Fetch once per vehicle; reopening the overlay must not refetch.
    if (fetchedFor.current === vehicle) return undefined;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let alive = true;

    setLoading(true);
    setDensityError(null);

    const load = fetchBom
      ? fetchBom(controller.signal).then((r) => {
        if (!r.ok) throw new Error(r.reason || 'unavailable');
        return r.data;
      })
      : getJson(`${API_BASE}/assembly/${vehicle}/bom`, controller.signal);

    load
      .then((data) => {
        if (!alive) return;
        const byId = new Map();
        for (const node of data?.nodes || []) {
          if (!node?.id) continue;
          byId.set(node.id, {
            count: Number(node.failure_count) || 0,
            linked: linkByPresence
              || (Array.isArray(node.kg_ids) ? node.kg_ids.length > 0 : false),
          });
        }
        fetchedFor.current = vehicle;
        setDensity(byId);
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        // Backend down is an expected state, not an exception to shout about.
        setDensityError(err?.message || 'unavailable');
        setDensity(null);
      })
      .finally(() => {
        clearTimeout(timer);
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [vehicle, enabled, fetchBom, linkByPresence]);

  /** Active DTCs only — resolved codes are history, not a live fault. */
  const activeDtcs = useMemo(
    () => (faultSet?.alerts ?? NO_ALERTS).filter((a) => a && a.status === 'active'),
    [faultSet],
  );

  /** node id -> {severity, codes: [...]} for the active codes. */
  const dtcByNode = useMemo(() => {
    const map = new Map();
    for (const alert of activeDtcs) {
      const targets = partMap[alert.id];
      if (!targets) continue;
      for (const nodeId of targets) {
        const next = worst(map.get(nodeId), { severity: alert.severity, codes: [] });
        const codes = (map.get(nodeId)?.codes || []).concat(alert.id);
        map.set(nodeId, { severity: next.severity, codes });
      }
    }
    return map;
  }, [activeDtcs, partMap]);

  /** Codes we could not place on the model — shown, never silently dropped. */
  const unmapped = useMemo(
    () => activeDtcs.filter((a) => !partMap[a.id]),
    [activeDtcs, partMap],
  );

  const densityFor = useCallback(
    (nodeId) => density?.get(nodeId) ?? null,
    [density],
  );

  return {
    density,
    densityFor,
    densityError,
    loading,
    activeDtcs,
    dtcByNode,
    unmapped,
    maxCount: useMemo(() => {
      let m = 0;
      if (density) for (const v of density.values()) if (v.count > m) m = v.count;
      return m;
    }, [density]),
  };
}
